'use client'

import { useEffect, useState } from 'react'

import { BannerApiResponse } from '@/lib/api-connector-strapi-cms/api/page-blog-home/pageBlogHomeApi.types'
import { PageCategoryArticle } from '@/lib/api-connector-strapi-cms/api/page-category/pageCategoryApi.types'
import { CardArticle } from '@/lib/ui-kit/components/CardArticle.component'
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/lib/ui-kit/components/ui/pagination'

const PER_PAGE = 20

type ListExternalArticlesProps = {
  allStories: PageCategoryArticle[]
  category: string
  banner: BannerApiResponse | undefined
  children: React.ReactNode
}

export const ListAllExternalStories = ({
  allStories,
  category,
  banner,
  children,
}: ListExternalArticlesProps) => {
  const [articles, setArticles] = useState(allStories)
  const [currentPage, setCurrentPage] = useState(1)
  const [isFirstMount, setIsFirstMount] = useState(true)

  useEffect(() => {
    if (!isFirstMount) {
      window.scrollTo(0, window.innerHeight / 2)
    } else {
      setIsFirstMount(false)
    }
  }, [currentPage])

  const articlesPageCurrent = articles.slice(
    (currentPage - 1) * PER_PAGE,
    currentPage * PER_PAGE,
  )

  const totalPages = Math.ceil(articles.length / PER_PAGE)

  return (
    <>
      <h2 className="paragraph-l px-6 xl:px-10">{`All ${category} stories`}</h2>
      {articles &&
        articlesPageCurrent.map((article, index) => {
          if (index === articlesPageCurrent.length - 2)
            return (
              <div key={article.title}>
                <CardArticle
                  category={category}
                  title={article.title}
                  description={article.description}
                  datePublication={article.datePublication}
                  linkArticle={article.linkArticle}
                  image={article.imageCard}
                  authorName="author"
                  contentLead={article.contentLead}
                  contentTop={article.contentTop}
                  contentMiddle={article.contentMiddle}
                  contentQuote={article.contentQuote}
                  contentBottom={article.contentBottom}
                />
                {banner?.background?.url && (
                  <div className="xl:hidden">{children}</div>
                )}
              </div>
            )

          return (
            <CardArticle
              key={article.title}
              category={category}
              title={article.title}
              description={article.description}
              datePublication={article.datePublication}
              linkArticle={article.linkArticle}
              image={article.imageCard}
              authorName="author"
              contentLead={article.contentLead}
              contentTop={article.contentTop}
              contentMiddle={article.contentMiddle}
              contentQuote={article.contentQuote}
              contentBottom={article.contentBottom}
            />
          )
        })}
      <div className="p-6 xl:pl-10 xl:pt-12">
        <Pagination>
          <PaginationContent>
            <div className="hidden xl:flex">
              {Array.from({ length: totalPages }, (_, i) => (
                <PaginationItem
                  className={`mr-3 flex cursor-pointer items-center space-x-4 rounded-full border px-7 py-5 ${i === currentPage - 1
                    ? ' border-gray-1000'
                    : 'border-gray-200'
                    }`}
                  key={i}
                  onClick={() => setCurrentPage(i + 1)}
                >
                  <PaginationLink className="">{i + 1}</PaginationLink>
                </PaginationItem>
              ))}
            </div>

            {articles.length > PER_PAGE && (
              <div className="flex flex-1 justify-between xl:items-center xl:justify-end xl:gap-4">
                <PaginationItem
                  className={`flex cursor-pointer items-center rounded-full`}
                  onClick={() =>
                    setCurrentPage((prevPage) => Math.max(prevPage - 1, 1))
                  }
                >
                  <PaginationPrevious isDisabled={currentPage === 1} />
                </PaginationItem>
                <PaginationItem
                  className={`flex cursor-pointer items-center rounded-full`}
                  onClick={() =>
                    setCurrentPage((prevPage) =>
                      Math.min(prevPage + 1, totalPages),
                    )
                  }
                >
                  <PaginationNext isDisabled={currentPage === totalPages} />
                </PaginationItem>
              </div>
            )}
          </PaginationContent>
        </Pagination>
      </div>
    </>
  )
}
